import { ENGINE_VERSION } from "@h2lca/core";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { METHOD_LABEL, fmt, type MethodId } from "@h2lca/core";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { ButtonLink, Button } from "@/components/ui/FormControls";
import { InfoTip } from "@/components/ui/InfoTip";
import { Menu } from "@/components/ui/Menu";
import { MethodBadge } from "@/components/workshop/Badges";
import { SAVE_HELP, formatDateTime, saveState } from "@/components/workshop/helpers";
import { useSave } from "@/components/workshop/useSave";
import { api, ApiError } from "@/lib/api";
import { useMaster } from "@/store/master";
import { useResult } from "@/store/useResult";
import { resultSummary, STEPS, stepIndex, useWorkshop, type SavedSummary } from "@/store/workshop";

interface Row {
  id: string;
  name: string;
  exampleId: string | null;
  method: string;
  summary: SavedSummary | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * 내 시나리오 , 작업 중인 산정과 보관함에 저장한 산정을 한 곳에서 연다.
 * 작업 중인 시나리오는 맨 위 행에 강조되어 서고, 아무것도 없으면 빈 상태 하나가 길을 안내한다.
 */
export function ScenariosPage() {
  const nav = useNavigate();
  const data = useMaster((s) => s.data);
  const origin = useMaster((s) => s.origin);
  const scenario = useWorkshop((s) => s.scenario);
  const step = useWorkshop((s) => s.step);
  const serverId = useWorkshop((s) => s.serverId);
  const dirty = useWorkshop((s) => s.dirty);
  const lastSavedAt = useWorkshop((s) => s.lastSavedAt);
  const restoreDraft = useWorkshop((s) => s.restoreDraft);
  const loadFromServer = useWorkshop((s) => s.loadFromServer);
  const result = useResult();
  const { save, saving, error: saveError } = useSave(result);

  const [err, setErr] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!useWorkshop.getState().scenario) restoreDraft();
  }, [restoreDraft]);

  const loadRows = useCallback(async () => {
    try {
      const r = await api<{ scenarios: Row[] }>("/scenarios");
      setRows(r.scenarios);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "시나리오 목록을 불러오지 못했습니다");
      setRows([]);
    }
  }, []);
  useEffect(() => {
    void loadRows();
  }, [loadRows, lastSavedAt]);

  const guard = () => !(scenario && dirty) || window.confirm("작업 중인 시나리오가 있습니다. 보관함에 저장하지 않은 내용은 사라집니다. 계속할까요?");
  const open = async (id: string) => {
    if (id === serverId) return nav("/app/work");
    if (!guard()) return;
    setBusy(id);
    setErr(null);
    try {
      await loadFromServer(id);
      nav("/app/work");
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "시나리오를 열지 못했습니다");
    } finally {
      setBusy(null);
    }
  };
  const remove = async (row: Row) => {
    if (!window.confirm(`'${row.name}' 을(를) 삭제할까요? 되돌릴 수 없습니다.`)) return;
    setBusy(row.id);
    setErr(null);
    try {
      await api(`/scenarios/${row.id}`, { method: "DELETE" });
      await loadRows();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "삭제하지 못했습니다");
    } finally {
      setBusy(null);
    }
  };

  const state = scenario ? saveState(serverId, dirty, lastSavedAt) : null;
  const shelf = (rows ?? []).filter((r) => r.id !== serverId);
  const empty = rows !== null && shelf.length === 0 && !scenario;

  return (
    <AppShell>
      <div className="page">
        <PageHeader
          title="내 시나리오"
          description="작업 중인 산정과 보관함에 저장한 시나리오를 열고 관리합니다."
          actions={
            <ButtonLink to="/app" variant="primary">
              새 산정 시작
            </ButtonLink>
          }
        />

        {origin === "bundled" && <p className="note note-warn text-footnote">서버의 참고 데이터를 불러오지 못해 프로그램에 내장된 기본값으로 계산합니다.</p>}
        {err && <p className="note note-danger text-footnote">{err}</p>}

        {empty ? (
          <div className="empty-state">
            <p className="text-lead font-semibold text-ink">아직 저장한 시나리오가 없습니다</p>
            <p className="max-w-form text-caption text-ink-subtle">산정 시작에서 생산방식과 저장 및 이송 경로를 고르면 기본 설비 구성이 만들어집니다. 예시나 빈 시나리오로 시작할 수도 있습니다.</p>
            <ButtonLink to="/app" variant="primary">
              새 산정 시작
            </ButtonLink>
          </div>
        ) : (
          rows !== null && (
            <div className="list-rows">
              {scenario && state && (
                <div className="list-row list-row-on">
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-inline">
                      <span className="text-body font-semibold text-ink">{scenario.name}</span>
                      <MethodBadge method={scenario.method} />
                      <span className="badge-accent">작업 중</span>
                    </p>
                    <p className={`mt-detail flex items-center gap-label text-footnote ${saveError ? "text-tier-tight" : state.tone === "ok" ? "text-ink-subtle" : "text-tier-moderate"}`}>
                      {stepIndex(step)} / {STEPS.length} 단계, {STEPS.find((s) => s.id === step)?.label}, {saveError ?? state.text}
                      <InfoTip label="저장 방식 안내">{SAVE_HELP}</InfoTip>
                    </p>
                  </div>
                  <ResultCell summary={result ? resultSummary(result) : null} />
                  <div className="list-row-actions">
                    {(state.tone !== "ok" || saveError) && (
                      <Button variant="soft" onClick={() => void save()} disabled={saving}>
                        {saving ? "저장 중…" : "보관함에 저장"}
                      </Button>
                    )}
                    <Button variant="primary" onClick={() => nav("/app/work")}>
                      이어서 하기
                    </Button>
                  </div>
                </div>
              )}
              {shelf.map((row) => (
                <div key={row.id} className="list-row">
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-inline">
                      <span className="text-body font-semibold text-ink">{row.name}</span>
                      <MethodBadge method={(row.method in METHOD_LABEL ? row.method : "general") as MethodId} />
                    </p>
                    <p className="mt-detail text-footnote text-ink-subtle">
                      보관함 저장 {formatDateTime(row.updatedAt)}
                      {row.exampleId && ", 예시에서 출발"}
                    </p>
                  </div>
                  <ResultCell summary={row.summary} />
                  <div className="list-row-actions">
                    <Button variant="soft" disabled={busy === row.id} onClick={() => void open(row.id)}>
                      열기
                    </Button>
                    <Menu label={`${row.name} 더 보기`} trigger={<span>더 보기</span>} items={[{ label: "삭제", danger: true, hint: "되돌릴 수 없습니다", onSelect: () => void remove(row) }]} />
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </AppShell>
  );
}

/** 행 오른쪽의 결과 칸 , 기능단위당 배출량과 총량. 값이 없으면 ',' */
function ResultCell({ summary }: { summary: SavedSummary | null }) {
  return (
    <div className="list-row-result">
      <p className="font-mono text-figure font-bold text-ink">
        {summary && summary.engineVersion !== ENGINE_VERSION ? "다시 열어 계산" : summary?.perKgH2 == null ? "없음" : fmt(summary.perKgH2, 2)} <span className="text-micro font-normal text-ink-muted">kgCO2eq/kgH2</span>
      </p>
      <p className="font-mono text-footnote text-ink-muted">{summary?.totalKgCO2eq == null ? "결과 없음" : `총 ${fmt(summary.totalKgCO2eq)} kgCO2eq`}</p>
    </div>
  );
}
